/**
 * SupporterCollaborationForm — NON-FINANCIAL "Express Interest" in awareness /
 * education / community collaboration. Renders an inline form (no modal, so no
 * overlay/overlap risk on mobile) plus the caller's previously submitted
 * interests. Carries no amount, payment or monetary field.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Send, CheckCircle2 } from 'lucide-react'
import { GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { cn } from '@/lib/utils'
import {
  expressInterest,
  getMyCollaborationInterests,
  COLLABORATION_STATUS_LABELS,
  type SupporterCollaborationInterest,
} from '@/services/supporterCollaborationService'

export interface CollaborationInterestOption {
  value: string
  label: string
}

export function SupporterCollaborationForm({
  interestOptions,
}: {
  interestOptions: CollaborationInterestOption[]
}) {
  const { t } = useTranslation()
  const [interestType, setInterestType] = useState(interestOptions[0]?.value ?? '')
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mine, setMine] = useState<SupporterCollaborationInterest[]>([])
  const [loadingMine, setLoadingMine] = useState(true)

  const loadMine = useCallback(async () => {
    setLoadingMine(true)
    try {
      setMine(await getMyCollaborationInterests())
    } catch {
      setMine([])
    } finally {
      setLoadingMine(false)
    }
  }, [])

  useEffect(() => {
    loadMine()
  }, [loadMine])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!interestType) return
    setSubmitting(true)
    setError(null)
    setSubmitted(false)
    try {
      await expressInterest({ interestType, title: title.trim() || undefined, message: message.trim() || undefined })
      setSubmitted(true)
      setTitle('')
      setMessage('')
      await loadMine()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('supporter.collaboration.error', 'Could not submit. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-5">
      <GovCard>
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          {t('supporter.collaboration.title', 'Express Interest in Collaboration')}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {t('supporter.collaboration.subtitle', 'Let our team know which awareness, education or community initiative you would like to take part in. This is not a financial sponsorship.')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="collab-type" className="block text-sm font-medium text-gray-700 mb-1">
              {t('supporter.collaboration.type', 'Area of interest')}
            </label>
            <select
              id="collab-type"
              value={interestType}
              onChange={(e) => setInterestType(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              {interestOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="collab-title" className="block text-sm font-medium text-gray-700 mb-1">
              {t('supporter.collaboration.titleLabel', 'Title (optional)')}
            </label>
            <input
              id="collab-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            />
          </div>

          <div>
            <label htmlFor="collab-message" className="block text-sm font-medium text-gray-700 mb-1">
              {t('supporter.collaboration.messageLabel', 'Message (optional)')}
            </label>
            <textarea
              id="collab-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={2000}
              rows={3}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
          {submitted && (
            <p className="text-sm text-emerald-600 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" />
              {t('supporter.collaboration.success', 'Your interest has been received. Our team will review it.')}
            </p>
          )}

          <GovButton variant="primary" type="submit" className="inline-flex items-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {t('supporter.collaboration.submit', 'Express Interest')}
          </GovButton>
        </form>
      </GovCard>

      <GovCard>
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          {t('supporter.collaboration.myInterests', 'My Expressed Interests')}
        </h3>
        {loadingMine ? (
          <div className="space-y-2">
            {[...Array(2)].map((_, i) => <div key={i} className="h-14 rounded-xl bg-gray-100 animate-pulse" />)}
          </div>
        ) : mine.length === 0 ? (
          <GovEmptyState icon="🤝" title={t('supporter.collaboration.none', 'No interests expressed yet')} description={t('supporter.collaboration.noneDesc', 'Use the form above to express interest in a collaboration.')} />
        ) : (
          <div className="space-y-2">
            {mine.map((i) => (
              <div key={i.id} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-gray-50">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{i.title || i.interestType}</p>
                  {i.message && <p className="text-sm text-gray-500 line-clamp-2 mt-0.5">{i.message}</p>}
                  <p className="text-xs text-gray-400 mt-1">{new Date(i.createdAt).toLocaleDateString('en-IN')}</p>
                </div>
                <span className={cn('text-xs px-2 py-1 rounded-full shrink-0', i.status === 'REVIEWED' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
                  {COLLABORATION_STATUS_LABELS[i.status] ?? i.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </GovCard>
    </div>
  )
}